"use client";

import Link from "next/link";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { normalizeChinaMobile } from "@/lib/chinaMobile";
import { parseApiError } from "@/lib/parseApiError";
import { validatePassword } from "@/lib/passwordValidation";
import type { PhoneCodeLoginAccent } from "@/components/auth/PhoneCodeLoginForm";
import type { AuthPortal } from "@/lib/portalTokens";
import { resetPasswordAndLogin, type LoginApiResponse } from "@/lib/loginApi";

const RESEND_COOLDOWN_SEC = 60;
const BACKEND = process.env.NEXT_PUBLIC_BACKEND_URL;

const accentStyles: Record<
  PhoneCodeLoginAccent,
  { ring: string; btn: string; link: string }
> = {
  indigo: {
    ring: "focus:ring-indigo-500",
    btn: "bg-indigo-600 hover:bg-indigo-700",
    link: "text-indigo-600",
  },
  green: {
    ring: "focus:ring-green-500",
    btn: "bg-green-600 hover:bg-green-700",
    link: "text-green-600",
  },
  blue: {
    ring: "focus:ring-blue-500",
    btn: "bg-blue-600 hover:bg-blue-700",
    link: "text-blue-600",
  },
};

async function requestVerificationCode(mobile: string): Promise<void> {
  const response = await fetch(`${BACKEND}/users/request-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mobile }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    const { message, retryAfterSeconds } = parseApiError(errorData, "验证码发送失败");
    const err = new Error(message) as Error & { retryAfterSeconds?: number };
    err.retryAfterSeconds = retryAfterSeconds;
    throw err;
  }
}

export type ForgotPasswordFormProps = {
  accent: PhoneCodeLoginAccent;
  portal: AuthPortal;
  loginHref: string;
  onSuccess: (data: LoginApiResponse) => void | Promise<void>;
};

export default function ForgotPasswordForm({
  accent,
  portal,
  loginHref,
  onSuccess,
}: ForgotPasswordFormProps) {
  const [step, setStep] = useState<"mobile" | "reset">("mobile");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const styles = accentStyles[accent];
  const inputClass = `w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${styles.ring}`;

  useLayoutEffect(() => {
    if (step !== "reset" || loading) return;
    codeInputRef.current?.focus({ preventScroll: true });
  }, [step, loading]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const startResendCooldown = useCallback((seconds: number = RESEND_COOLDOWN_SEC) => {
    setResendCooldown(seconds);
  }, []);

  const sendCode = useCallback(
    async (trimmedMobile: string) => {
      await requestVerificationCode(trimmedMobile);
      setStep("reset");
      setMobile(trimmedMobile);
      setCode("");
      startResendCooldown(RESEND_COOLDOWN_SEC);
    },
    [startResendCooldown],
  );

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedMobile = normalizeChinaMobile(mobile);
    if (!mobile.trim()) {
      setError("请输入手机号");
      return;
    }
    if (!normalizedMobile) {
      setError("请输入正确的中国大陆手机号");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await sendCode(normalizedMobile);
    } catch (err) {
      const retryAfter =
        err instanceof Error
          ? (err as Error & { retryAfterSeconds?: number }).retryAfterSeconds
          : undefined;
      if (retryAfter !== undefined && retryAfter > 0 && step === "reset") {
        startResendCooldown(retryAfter);
      }
      setError(err instanceof Error ? err.message : "验证码发送失败");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || resendLoading || !mobile) return;

    setResendLoading(true);
    setError(null);
    try {
      await sendCode(mobile);
    } catch (err) {
      const retryAfter =
        err instanceof Error
          ? (err as Error & { retryAfterSeconds?: number }).retryAfterSeconds
          : undefined;
      if (retryAfter !== undefined && retryAfter > 0) {
        startResendCooldown(retryAfter);
      }
      setError(err instanceof Error ? err.message : "验证码发送失败");
    } finally {
      setResendLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError("请输入验证码");
      return;
    }
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== passwordConfirm) {
      setError("两次输入的密码不一致");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await resetPasswordAndLogin(
        mobile,
        code.trim(),
        password,
        passwordConfirm,
        portal,
      );
      await onSuccess(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "重置密码失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {step === "mobile" ? (
        <form onSubmit={(e) => void handleSendCode(e)}>
          <div className="flex flex-col gap-4">
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="输入手机号"
              className={inputClass}
            />
            <button
              type="submit"
              disabled={!mobile.trim() || loading}
              className={`w-full px-6 py-3 text-white rounded-lg disabled:opacity-50 transition-colors ${styles.btn}`}
            >
              {loading ? "正在发送" : "获取验证码"}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={(e) => void handleReset(e)}>
          <div className="flex flex-col gap-4">
            <input type="text" value={mobile} disabled className={`${inputClass} bg-gray-100`} />
            <div className="flex items-center gap-3">
              <input
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="输入验证码"
                className={`flex-1 min-w-0 p-3 border rounded-lg focus:outline-none focus:ring-2 ${styles.ring}`}
              />
              {resendCooldown > 0 ? (
                <span className={`text-sm whitespace-nowrap shrink-0 ${styles.link}`}>
                  {resendCooldown} 秒后可重新获取
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleResendCode()}
                  disabled={resendLoading}
                  className={`text-sm whitespace-nowrap shrink-0 hover:underline disabled:opacity-50 ${styles.link}`}
                >
                  {resendLoading ? "正在发送" : "重新获取验证码"}
                </button>
              )}
            </div>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="新密码（8–32 位，含字母和数字）"
              className={inputClass}
            />
            <input
              type="password"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="确认新密码"
              className={inputClass}
            />
            <button
              type="submit"
              disabled={!code.trim() || !password || !passwordConfirm || loading}
              className={`w-full px-6 py-3 text-white rounded-lg disabled:opacity-50 transition-colors ${styles.btn}`}
            >
              {loading ? "正在重置" : "重置密码并登录"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("mobile");
                setCode("");
                setPassword("");
                setPasswordConfirm("");
                setResendCooldown(0);
                setError(null);
              }}
              className={`w-full hover:underline text-center ${styles.link}`}
            >
              更换手机号
            </button>
          </div>
        </form>
      )}

      {error && <p className="text-red-500 mt-4 text-center">{error}</p>}

      <p className="mt-6 text-center text-sm text-gray-600">
        <Link href={loginHref} className={`hover:underline ${styles.link}`}>
          返回登录
        </Link>
      </p>
    </div>
  );
}
